import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import { useClientsQuery } from "Clients/Api/Queries/useClientsQuery";
import {
  CLIENTS_SORTABLE_COLUMNS,
  useClientsTableColumns,
} from "Clients/Components/useClientsTableColumns";
import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  ClientListSortColumn,
  ClientStatus,
  SortOrder,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { usePartnersQuery } from "Partners/Api/Queries/usePartnersQuery";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

const SORT_COLUMN_MAP: Record<string, ClientListSortColumn> = {
  companyName: ClientListSortColumn.CompanyName,
  partnerName: ClientListSortColumn.PartnerName,
  projectCount: ClientListSortColumn.ProjectCount,
  email: ClientListSortColumn.Email,
  phone: ClientListSortColumn.Phone,
};

export const ClientsTable = (): React.ReactNode => {
  const { t } = useResource();
  const { isSysAdmin } = useCurrentUser();

  const [selectedPartnerID, setSelectedPartnerID] = useQueryState(
    "partner",
    parseAsString,
  );

  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<ClientStatus>(Object.values(ClientStatus)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({ validSortIds: [...CLIENTS_SORTABLE_COLUMNS] });

  const sortBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data: clientsData, isLoading } = useClientsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || null,
    partnerIDs:
      isSysAdmin && selectedPartnerID ? [selectedPartnerID] : undefined,
    sortBy,
    sortOrder,
    statusFilter: selectedStatus ? [selectedStatus] : null,
  });

  const { data: partnersData } = usePartnersQuery({
    offset: 0,
    limit: 1000,
    enabled: isSysAdmin,
  });

  const clients = clientsData?.items ?? [];
  const totalCount = clientsData?.totalCount ?? 0;

  const partnerOptions: BlOption[] = useMemo(() => {
    const partners = partnersData?.items ?? [];
    return [
      { value: "", label: t(Resources.Clients.AllPartners) },
      ...partners.map((p) => ({ value: p.partnerID, label: p.companyName })),
    ];
  }, [partnersData, t]);

  const statusOptions = [
    { value: "", label: t(Resources.Common.AllStatuses) },
    { value: ClientStatus.Active, label: t(Resources.Common.StatusActive) },
    {
      value: ClientStatus.Inactive,
      label: t(Resources.Common.StatusInactive),
    },
  ];

  const columns = useClientsTableColumns(isSysAdmin);

  const table = useReactTable({
    data: clients,
    columns,
    getRowId: (row) => row.clientID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        spacing={Spacing.S}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="client-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Clients.SearchClientsPlaceholder)}
          />
        </Box>
        {isSysAdmin && (
          <Box sx={{ minWidth: 200 }}>
            <BlDropdown
              name="partner-filter"
              options={partnerOptions}
              value={selectedPartnerID ?? ""}
              onChange={(value) => {
                setSelectedPartnerID((value as string) || null);
                resetPage();
              }}
              placeholder={t(Resources.Common.Partners)}
            />
          </Box>
        )}
        <Box sx={{ minWidth: 180 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus((value as ClientStatus) || null);
              resetPage();
            }}
            placeholder={t(Resources.Common.AllStatuses)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
